import { useCallback, useEffect, useRef, useState } from "react";
import type { PointerEvent as ReactPointerEvent } from "react";

import { ONBOARDING_AGENT_TRIGGER_SIZE } from "../../OnboardingAgent/constants";
import { clampPositionToViewport } from "../../OnboardingAgent/helpers/OnboardingAgentPosition.helpers";
import type { OnboardingAgentViewportPosition } from "../../OnboardingAgent/OnboardingAgent.types";
import { ONBOARDING_AGENT_DRAG_ACTIVATION_DISTANCE_PX } from "../constants";

type PointerDragState = {
    readonly pointerId: number;
    readonly startX: number;
    readonly startY: number;
    readonly origin: OnboardingAgentViewportPosition;
};

type UseOnboardingAgentTriggerDragParams = {
    readonly position: OnboardingAgentViewportPosition;
    readonly onDragMove?: (position: OnboardingAgentViewportPosition) => void;
    readonly onDragEnd: (position: OnboardingAgentViewportPosition) => void;
};

type UseOnboardingAgentTriggerDragResult = {
    /** Live position while dragging; `null` when the trigger follows its committed position. */
    readonly dragPosition: OnboardingAgentViewportPosition | null;
    readonly isDragging: boolean;
    readonly handlePointerDown: (event: ReactPointerEvent<HTMLElement>) => void;
    /** Reports whether the press that just ended was a drag, so the click it produces can be ignored. */
    readonly consumeDragSuppression: () => boolean;
};

const MOUSE_PRIMARY_BUTTON = 0;

const trySetPointerCapture = (element: Element, pointerId: number): void => {
    if (typeof element.setPointerCapture !== "function") {
        return;
    }

    try {
        element.setPointerCapture(pointerId);
    } catch {
        // jsdom and a pointer that is already inactive reject capture; window listeners still track the gesture.
    }
};

const tryReleasePointerCapture = (element: Element | null, pointerId: number): void => {
    if (
        !element ||
        typeof element.hasPointerCapture !== "function" ||
        typeof element.releasePointerCapture !== "function"
    ) {
        return;
    }

    try {
        if (element.hasPointerCapture(pointerId)) {
            element.releasePointerCapture(pointerId);
        }
    } catch {
        // The pointer is already gone, which is the case this release is cleaning up.
    }
};

export const useOnboardingAgentTriggerDrag = ({
    position,
    onDragMove,
    onDragEnd,
}: UseOnboardingAgentTriggerDragParams): UseOnboardingAgentTriggerDragResult => {
    const [dragPosition, setDragPosition] = useState<OnboardingAgentViewportPosition | null>(null);
    const pointerStateRef = useRef<PointerDragState | null>(null);
    const hasDraggedRef = useRef(false);
    const pendingPositionRef = useRef<OnboardingAgentViewportPosition | null>(null);
    const animationFrameRef = useRef<number | null>(null);
    const detachDragListenersRef = useRef<(() => void) | null>(null);
    const captureTargetRef = useRef<Element | null>(null);
    const onDragMoveRef = useRef(onDragMove);
    const onDragEndRef = useRef(onDragEnd);
    onDragMoveRef.current = onDragMove;
    onDragEndRef.current = onDragEnd;

    const cancelPendingFrame = useCallback((): void => {
        if (animationFrameRef.current !== null) {
            window.cancelAnimationFrame(animationFrameRef.current);
            animationFrameRef.current = null;
        }
    }, []);

    const stopTrackingPointer = useCallback((): void => {
        detachDragListenersRef.current?.();
        detachDragListenersRef.current = null;
        pointerStateRef.current = null;
        cancelPendingFrame();
    }, [cancelPendingFrame]);

    const commitActiveGesture = useCallback((): void => {
        const pointerState = pointerStateRef.current;
        if (!pointerState) {
            return;
        }

        const captureTarget = captureTargetRef.current;
        stopTrackingPointer();
        captureTargetRef.current = null;
        tryReleasePointerCapture(captureTarget, pointerState.pointerId);

        const finalPosition = pendingPositionRef.current;
        pendingPositionRef.current = null;
        setDragPosition(null);

        if (hasDraggedRef.current && finalPosition) {
            onDragEndRef.current(finalPosition);
        }
    }, [stopTrackingPointer]);

    const commitActiveGestureRef = useRef(commitActiveGesture);
    commitActiveGestureRef.current = commitActiveGesture;

    useEffect(() => stopTrackingPointer, [stopTrackingPointer]);

    const handlePointerDown = useCallback(
        (event: ReactPointerEvent<HTMLElement>): void => {
            if (event.pointerType === "mouse" && event.button !== MOUSE_PRIMARY_BUTTON) {
                return;
            }

            // A previous gesture can still be open when pointerup never reached the page (released
            // outside the window, or over a cross-origin frame). Commit it before starting another.
            commitActiveGestureRef.current();
            hasDraggedRef.current = false;
            pendingPositionRef.current = null;
            setDragPosition(null);
            pointerStateRef.current = {
                pointerId: event.pointerId,
                startX: event.clientX,
                startY: event.clientY,
                origin: position,
            };
            captureTargetRef.current = event.currentTarget;
            // Capture delivers pointerup even after the pointer leaves the browser window. Listeners
            // stay on window so a re-render of the button does not drop the gesture.
            trySetPointerCapture(event.currentTarget, event.pointerId);

            const handleMove = (moveEvent: PointerEvent): void => {
                const pointerState = pointerStateRef.current;
                if (!pointerState || pointerState.pointerId !== moveEvent.pointerId) {
                    return;
                }

                const deltaX = moveEvent.clientX - pointerState.startX;
                const deltaY = moveEvent.clientY - pointerState.startY;

                if (
                    !hasDraggedRef.current &&
                    Math.hypot(deltaX, deltaY) < ONBOARDING_AGENT_DRAG_ACTIVATION_DISTANCE_PX
                ) {
                    return;
                }

                hasDraggedRef.current = true;
                pendingPositionRef.current = clampPositionToViewport(
                    { x: pointerState.origin.x + deltaX, y: pointerState.origin.y + deltaY },
                    ONBOARDING_AGENT_TRIGGER_SIZE,
                    ONBOARDING_AGENT_TRIGGER_SIZE,
                );

                if (animationFrameRef.current !== null) {
                    return;
                }

                animationFrameRef.current = window.requestAnimationFrame(() => {
                    animationFrameRef.current = null;
                    const nextPosition = pendingPositionRef.current;
                    if (!pointerStateRef.current || !nextPosition) {
                        return;
                    }

                    setDragPosition(nextPosition);
                    onDragMoveRef.current?.(nextPosition);
                });
            };

            const handleUp = (upEvent: PointerEvent): void => {
                const pointerState = pointerStateRef.current;
                if (!pointerState || pointerState.pointerId !== upEvent.pointerId) {
                    return;
                }

                commitActiveGestureRef.current();
            };

            const handleBlur = (): void => {
                commitActiveGestureRef.current();
            };

            window.addEventListener("pointermove", handleMove);
            window.addEventListener("pointerup", handleUp);
            window.addEventListener("pointercancel", handleUp);
            window.addEventListener("blur", handleBlur);
            detachDragListenersRef.current = (): void => {
                window.removeEventListener("pointermove", handleMove);
                window.removeEventListener("pointerup", handleUp);
                window.removeEventListener("pointercancel", handleUp);
                window.removeEventListener("blur", handleBlur);
            };
        },
        [position],
    );

    const consumeDragSuppression = useCallback((): boolean => {
        if (!hasDraggedRef.current) {
            return false;
        }

        hasDraggedRef.current = false;

        return true;
    }, []);

    return {
        dragPosition,
        isDragging: dragPosition !== null,
        handlePointerDown,
        consumeDragSuppression,
    };
};
